import { useMemo, useRef, useState, useEffect } from 'react';
import { useAtomValue, useSetAtom, atom } from 'jotai';
import { Popup } from 'react-map-gl';
import mapboxGl from 'mapbox-gl';
import WebMercatorViewport from 'viewport-mercator-project';
import { Map as MapComponent, PolygonArea } from 'components/map';
import { getMapDefaultConfig } from 'components/map/atoms';
import { MapUserConfig } from 'components/map/types';
import { buildPolygonStyle } from 'lib/map';
import {
  useMapHoverHandler,
  LayerProcessor,
} from 'lib/hooks/useMapHoverHandler';
import {
  referenceLocationGeometriesAtom,
  documentLocationsAtom,
  selectedEstablishmentIdsAtom,
  establishmentsArrayAtom,
  hoveredEstablishmentRefLocationIdAtom,
} from 'atoms/establishments-atoms';
import styles from './EstablishmentsMap.module.scss';

const VIEWPORT_PADDING = 40;
const DEFAULT_MAP_WIDTH = 400;
const DEFAULT_MAP_HEIGHT = 350;

const DOCUMENT_LOCATION_COLOR = '#012749';
const REF_LOCATION_SELECTED_COLOR = '#90EE90';
const REF_LOCATION_HOVERED_COLOR = '#FF8C00';

interface GeometryFeature {
  type: 'document' | 'reference';
  name: string;
  id: string;
}

export const EstablishmentsMap = () => {
  const referenceLocations = useAtomValue(referenceLocationGeometriesAtom);
  const documentLocations = useAtomValue(documentLocationsAtom);
  const establishments = useAtomValue(establishmentsArrayAtom);
  const selectedIds = useAtomValue(selectedEstablishmentIdsAtom);
  const hoveredRefLocationId = useAtomValue(
    hoveredEstablishmentRefLocationIdAtom
  );
  const setHoveredRefLocationId = useSetAtom(
    hoveredEstablishmentRefLocationIdAtom
  );

  const wrapperRef = useRef<HTMLDivElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [dimensions, setDimensions] = useState({
    width: DEFAULT_MAP_WIDTH,
    height: DEFAULT_MAP_HEIGHT,
  });

  useEffect(() => {
    if (!wrapperRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { rootMargin: '50px' }
    );

    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!wrapperRef.current) return;

    const observer = new ResizeObserver(() => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      const legendHeight = legendRef.current?.offsetHeight || 0;
      setDimensions({
        width: rect.width || DEFAULT_MAP_WIDTH,
        height: Math.max(rect.height - legendHeight, 100),
      });
    });

    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  const mapConfigAtom = useMemo(() => {
    const defaultConfig = getMapDefaultConfig();
    return atom({
      ...defaultConfig,
      wells: { ...defaultConfig.wells, checked: false },
      wellsV2: { ...defaultConfig.wellsV2, checked: false },
      developmentAreas: {
        ...defaultConfig.developmentAreas,
        options: {
          ...defaultConfig.developmentAreas.options,
          horizontalUnits: false,
          verticalUnits: false,
          fieldUnits: false,
          pricing: false,
        } as MapUserConfig['developmentAreas']['options'],
      },
      titleEntityAssets: {
        ...defaultConfig.titleEntityAssets,
        checked: false,
      },
      basinTier: { ...defaultConfig.basinTier, checked: false },
      scrollZoom: { checked: true },
      popups: { checked: false },
      focusAreas: { checked: false, selections: {} },
    });
  }, []);

  const selectedRefLocationIds = useMemo(() => {
    if (!selectedIds || !establishments.length) return new Set<number>();
    const selectedSet = new Set(selectedIds);
    return new Set(
      establishments
        .filter((e) => selectedSet.has(e.id))
        .map((e) => e.referenceLocationId)
    );
  }, [selectedIds, establishments]);

  const mapViewState = useMemo(() => {
    const allGeometries = [
      ...documentLocations.map((l) => l.geometryJson),
      ...referenceLocations.map((l) => l.geometryJson),
    ].filter((geom): geom is GeoJSON.Geometry => geom !== null);

    if (allGeometries.length === 0) return null;

    try {
      const coordinates = extractCoordinates(allGeometries);
      if (coordinates.length === 0) return null;

      const bounds = calculateBounds(coordinates);
      const mercator = new WebMercatorViewport({
        width: dimensions.width,
        height: dimensions.height,
      });

      const viewState = mercator.fitBounds(
        [
          [bounds.getWest(), bounds.getSouth()],
          [bounds.getEast(), bounds.getNorth()],
        ],
        { padding: VIEWPORT_PADDING }
      );

      return {
        longitude: viewState.longitude,
        latitude: viewState.latitude,
        zoom: viewState.zoom,
      };
    } catch (error) {
      console.error('Error calculating map view:', error);
      return null;
    }
  }, [documentLocations, referenceLocations, dimensions]);

  const visibleRefLocations = useMemo(() => {
    return referenceLocations.filter(
      (loc) =>
        selectedRefLocationIds.has(loc.id) || loc.id === hoveredRefLocationId
    );
  }, [referenceLocations, selectedRefLocationIds, hoveredRefLocationId]);

  const getRefLocationStyle = (refLocationId: number) => {
    if (selectedRefLocationIds.has(refLocationId)) {
      return buildPolygonStyle({
        color: REF_LOCATION_SELECTED_COLOR,
        fillOpacity: 0.25,
        lineOpacity: 0.8,
        lineWidth: 2,
      });
    }
    return buildPolygonStyle({
      color: REF_LOCATION_HOVERED_COLOR,
      fillOpacity: 0.25,
      lineOpacity: 0.8,
      lineWidth: 2,
    });
  };

  const documentLocationStyle = useMemo(
    () => buildPolygonStyle({ color: DOCUMENT_LOCATION_COLOR }),
    []
  );

  const refLocationNameMap = useMemo(() => {
    const map = new Map<number, string>();
    referenceLocations.forEach((loc) => map.set(loc.id, loc.name));
    return map;
  }, [referenceLocations]);

  const docLocationNameMap = useMemo(() => {
    const map = new Map<number, string>();
    documentLocations.forEach((loc) => map.set(loc.id, loc.name));
    return map;
  }, [documentLocations]);

  const layerProcessors: LayerProcessor<GeometryFeature>[] = useMemo(
    () => [
      {
        prefix: 'polygon-fill-ref-',
        isVisible: true,
        extractId: (layerId) => layerId.replace('polygon-fill-ref-', ''),
        buildFeature: (idStr) => {
          const numericId = parseInt(idStr, 10);
          if (!isNaN(numericId)) {
            return {
              type: 'reference',
              name:
                refLocationNameMap.get(numericId) ||
                `Reference Location ${numericId}`,
              id: `ref-${numericId}`,
            };
          }
          return null;
        },
      },
      {
        prefix: 'polygon-fill-doc-',
        isVisible: true,
        extractId: (layerId) => layerId.replace('polygon-fill-doc-', ''),
        buildFeature: (idStr) => {
          const numericId = parseInt(idStr, 10);
          if (!isNaN(numericId)) {
            return {
              type: 'document',
              name:
                docLocationNameMap.get(numericId) ||
                `Document Location ${numericId}`,
              id: `doc-${numericId}`,
            };
          }
          return null;
        },
      },
    ],
    [refLocationNameMap, docLocationNameMap]
  );

  const { hoverInfo, onMouseMove, onMouseLeave } =
    useMapHoverHandler(layerProcessors);

  // Sync map hover to the shared atom
  useEffect(() => {
    if (hoverInfo) {
      const refFeature = hoverInfo.features.find((f) => f.type === 'reference');
      if (refFeature) {
        const id = parseInt(refFeature.id.replace('ref-', ''), 10);
        setHoveredRefLocationId(id);
        return;
      }
    }
    setHoveredRefLocationId(null);
  }, [hoverInfo, setHoveredRefLocationId]);

  const interactiveLayerIds = useMemo(() => {
    const ids: string[] = [];
    visibleRefLocations.forEach((loc) => {
      ids.push(`polygon-fill-ref-${loc.id}`);
    });
    documentLocations.forEach((loc) => {
      ids.push(`polygon-fill-doc-${loc.id}`);
    });
    return ids;
  }, [visibleRefLocations, documentLocations]);

  if (!documentLocations.length && !referenceLocations.length) {
    return (
      <div ref={wrapperRef} className={styles.emptyState}>
        No geometry data available
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <div ref={legendRef} className={styles.legend}>
        <div className={styles.legendItem}>
          <span className={`${styles.legendSwatch} ${styles.documentSwatch}`} />
          <span>Document Location</span>
        </div>
        <div className={styles.legendItem}>
          <span
            className={`${styles.legendSwatch} ${styles.referenceSwatch}`}
          />
          <span>Selected Establishment</span>
        </div>
      </div>
      {isVisible && mapViewState ? (
        <div className={styles.mapArea}>
          <MapComponent
            longitude={mapViewState.longitude}
            latitude={mapViewState.latitude}
            zoom={mapViewState.zoom}
            mapConfigAtom={mapConfigAtom}
            showControls={true}
            showMapFilters={false}
            blockPopups={true}
            interactiveLayerIds={interactiveLayerIds}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
          >
            {documentLocations
              .filter((location) => location.geometryJson !== null)
              .map((location) => (
                <PolygonArea
                  key={`doc-${location.id}`}
                  id={`doc-${location.id}`}
                  geometry={location.geometryJson ?? undefined}
                  style={documentLocationStyle}
                />
              ))}

            {visibleRefLocations
              .filter((refLoc) => refLoc.geometryJson !== null)
              .map((refLoc) => (
                <PolygonArea
                  key={`ref-${refLoc.id}`}
                  id={`ref-${refLoc.id}`}
                  geometry={refLoc.geometryJson ?? undefined}
                  style={getRefLocationStyle(refLoc.id)}
                />
              ))}

            {hoverInfo && (
              <Popup
                longitude={hoverInfo.longitude}
                latitude={hoverInfo.latitude}
                closeButton={false}
                closeOnClick={false}
                className={styles.hoverPopup}
              >
                <div className={styles.popupContent}>
                  {hoverInfo.features.map((feature, idx) => (
                    <div
                      key={`${feature.id}-${idx}`}
                      className={styles.popupItem}
                    >
                      <span
                        className={`${styles.popupSwatch} ${
                          feature.type === 'document'
                            ? styles.documentSwatch
                            : styles.referenceSwatch
                        }`}
                      />
                      <span>{feature.name}</span>
                    </div>
                  ))}
                </div>
              </Popup>
            )}
          </MapComponent>
        </div>
      ) : (
        <div className={styles.emptyState}>Loading map...</div>
      )}
    </div>
  );
};

function extractCoordinates(geometries: GeoJSON.Geometry[]): number[][] {
  return geometries.flatMap((geom) => {
    if (!geom) return [];
    if (geom.type === 'Polygon') {
      return (geom.coordinates as number[][][]).flat(1);
    } else if (geom.type === 'MultiPolygon') {
      return (geom.coordinates as number[][][][]).flat(2);
    }
    return [];
  });
}

function calculateBounds(coordinates: number[][]): mapboxGl.LngLatBounds {
  return coordinates.reduce(
    (bounds, coordinate) => bounds.extend([coordinate[0], coordinate[1]]),
    new mapboxGl.LngLatBounds()
  );
}
